import React, { useContext, useState, useEffect } from "react"
import styled from "styled-components"

import { nav, footer, breakpoints as bp } from "./layout"
import Nav from "./nav"
import Footer from "./footer"
import FooterPro from "./FootPro"
import SEO from "./seo"
import GlobalStyle from "./globalStyle"
import LocaleContext from "../context/localeContext"
import PageContext from "../context/pageContext"
import PageContextProps from "../context/pageContextProps"
import "./global.css"
import { MetadataFragment } from "../../types/graphql-types"
import colors from "../utils/colors"
import { navigate } from "gatsby"
import supportedLocales from "../locales"
// tslint:disable-next-line:no-implicit-dependencies : @reach/router is part of Gatsby
import { useLocation } from "@reach/router"
import Cookies from "js-cookie"

interface Props {
  children: JSX.Element[] | JSX.Element
  title: string
  metadata?: MetadataFragment | null
  activeTab?: string
  isInverted?: boolean
  isFooterInverted?: boolean
  hideNav?: boolean
  hideFooter?: boolean
  backgroundColor?: string
  hasBottomMargin?: boolean
  currencyData?: any
}

const BOTTOM_MARGIN = "50px"

export const NavSpacer = styled.div`
  @media (max-width: ${bp.mobileMax}) {
    padding-top: ${nav.mobile};
  }
  @media (min-width: ${bp.tabletMin}) {
    padding-top: ${nav.desktop};
  }
`

const ContentContainer = styled.div<{ backgroundColor?: string; hasBottomMargin?: boolean }>`
  ${props => (props.backgroundColor ? `background-color: ${props.backgroundColor};` : "")}

  @media (max-width: ${bp.mobileMax}) {
    min-height: calc(100vh - ${footer.mobile} ${props =>
  props.hasBottomMargin ? `- ${BOTTOM_MARGIN}` : ""});
  }
  @media (min-width: ${bp.tabletMin}) {
    min-height: calc(100vh - ${footer.desktop} ${props =>
  props.hasBottomMargin ? `- ${BOTTOM_MARGIN}` : ""});
  }
  a, a:hover, a:focus, a:visited, a:active {
    color: ${colors.teal}
  }
`

const BottomMargin = styled.div`
  margin-bottom: ${BOTTOM_MARGIN};
`

const Page = ({
  children,
  title,
  metadata,
  activeTab,
  isInverted,
  isFooterInverted,
  hideNav,
  hideFooter,
  backgroundColor,
  hasBottomMargin,
}: Props) => {
  const locale = useContext<string>(LocaleContext)
  const { isSimple } = useContext<PageContextProps>(PageContext)
  // get user location from cookie
  const locationCookie: string | undefined = Cookies.get("hmdconnect_location")
  // get user selected language from localStorage

  const localeStoredLang =
    typeof window !== "undefined" && window.localStorage.getItem("userSelectLanguage")

  // validate the userSelected
  const userSelectedLocale =
    localeStoredLang && supportedLocales.includes(localeStoredLang) ? localeStoredLang : "en"

  const detectedLocale =
    locationCookie && supportedLocales.includes(locationCookie.toLocaleLowerCase())
      ? locationCookie.toLocaleLowerCase()
      : "en"

  const [redirectLocale, setRedirectLocale] = useState<string>(userSelectedLocale || detectedLocale)
  const [redirectPath, setRedirectPath] = useState<string | null>(null)
  const pagePath = useLocation().pathname
  const pageName = pagePath.replace(new RegExp(`${locale}\/?`), "")
  useEffect(() => {
    setRedirectLocale(userSelectedLocale)
  }, [])

  useEffect(() => {
    /**
     * @todo This will need to be removed when fi translations are complete.
     * This is a forced redirect so all /fi pages, user selected or location detected
     * all go to en
     */
    if (locale === "fi" || redirectLocale === "fi") {
      // Just keep them away from fi page
      if (locale === "fi") {
        setRedirectPath(`${pageName}`)
      }
    } else if (
      locale === "en" &&
      userSelectedLocale !== "en" &&
      redirectLocale !== "en" &&
      redirectLocale !== "uk"
    ) {
      setRedirectPath(`/${redirectLocale}${pageName}`)
    }
  }, [])

  useEffect(() => {
    redirectPath && navigate(redirectPath)
  }, [redirectPath])

  return (
    <>
      <GlobalStyle />
      <SEO
        title={metadata?.title?.heading || title}
        lang={locale}
        description={metadata?.description?.heading}
        image={metadata?.image?.file?.url}
      />
      {!hideNav && !isSimple && <Nav activeTab={activeTab} isInverted={isInverted} />}
      <ContentContainer backgroundColor={backgroundColor} hasBottomMargin={hasBottomMargin}>
        {children}
      </ContentContainer>
      {hasBottomMargin && <BottomMargin />}
      {!isSimple && !hideFooter && <Footer isInverted={isInverted} />}
    </>
  )
}

export default Page
